/* ============================================================================
   BCP Bajaj — bcp-horizon-cards.js   (Horizon theme, draft 189523788069)
   "Chatpata Modern" product-card redesign — Direction B (Horizon-native robust
   scoping/hydration) with Direction A's bold visual values grafted into the CSS.

   Loads GLOBALLY via a footer custom-liquid <script>. Self-contained, defensive,
   correct-by-construction (no live preview). Pairs with bcp-horizon-cards.css.

   What it does, per <product-card> in a GRID context (never in the cart drawer,
   cart-items, or a <dialog>):
     1) Read the VISIBLE title text from [ref="productTitleLink"] [role="heading"]
        (fallbacks: title-link anchor text, or the product handle from /products/H).
     2) Resolve a per-product flavour colour (fill + AA-precomputed text) from the
        tokenised title, robust to handle/title mismatch. Set --bcp-pcolor /
        --bcp-ptext on the <product-card> + add the .bcp-hz marker class.
     3) Wrap the heading's child nodes in span.bcp-title-box (kept INSIDE the
        heading so role="heading" is preserved) + add .bcp-titled for the 2-line clamp.
     4) If on sale, inject ONE terracotta SAVE pill (absolute child of the card)
        and tag the struck compare / sale price for robust colouring. Sale is
        detected ONLY by a real struck compare element (<s>/<del>/compare class) —
        NEVER by "two distinct money values", so a variant price RANGE never fakes
        a sale.
     5) Inject a .bcp-feats chip row right after the title block.

   Guarantees:
     - Never removes/reparents a.product-card__link, the gallery, or the price DOM.
     - Injected .bcp-buy Add-to-Cart triggers Horizon's own native quick-add submit
       button (.quick-add__button--add, present whenever settings.quick_add is on -
       true by default) so the real cart-update event fires and the header cart-icon
       bubble + cart-items-component (drawer/cart page) refresh without a manual
       reload; falls back to a direct /cart/add.js call only if that control is
       missing or disabled. There is deliberately NO Buy Now on browse cards -
       every path goes Add to Cart -> cart drawer -> checkout, so the shopper
       always sees the free-gift tiers and the prepaid 5% nudge before paying.
       Never touches swatches or hydration wiring otherwise.
     - Idempotent (all builders re-check their own marks). Fully try/caught.
     - MutationObserver (debounced) enhances cards Horizon hydrates/re-renders and
       RECOVERS in-place re-renders that strip our marks; skips mutations inside
       our own .bcp-* nodes and inside cart regions.
   ============================================================================ */
(function () {
  if (window.__bcpHzCards) return;
  window.__bcpHzCards = true;

  var NAVY = { c: "#122154", t: "#ffffff" };
  var TEAL = "#108474";

  /* Flavour colour map — fill -> AA-precomputed text. Keys are lowercase tokens.
     Multi-word keys (containing '-') are matched against the joined token string. */
  var FLAVOURS = [
    ["aampapad",     { c: "#E8A33D", t: "#122154" }],
    ["aam-papad",    { c: "#E8A33D", t: "#122154" }],
    ["aampachak",    { c: "#E8A33D", t: "#122154" }],
    ["aam",          { c: "#E8A33D", t: "#122154" }],
    ["mango",        { c: "#E8A33D", t: "#122154" }],
    ["amla",         { c: "#6FA287", t: "#122154" }],
    ["hing",         { c: "#8A5E2A", t: "#ffffff" }],
    ["anardana",     { c: "#A8493C", t: "#ffffff" }],
    ["anar",         { c: "#A8493C", t: "#ffffff" }],
    ["jaljira",      { c: "#108474", t: "#ffffff" }],
    ["jal-jira",     { c: "#108474", t: "#ffffff" }],
    ["tikiya",       { c: "#D99A2B", t: "#122154" }],
    ["tasty",        { c: "#D99A2B", t: "#122154" }],
    ["mukhwas",      { c: "#9B5A8C", t: "#ffffff" }],
    ["saunf",        { c: "#9B5A8C", t: "#ffffff" }]
  ];
  var BUNDLES = ["combo", "gift", "fiesta", "duo", "perfect-pair", "perfect", "hamper", "bundle"];

  /* Feature chips — content per product; consistent format everywhere. */
  var FEATURES = [
    ["aampapad",   ["Dried Mango", "Chewy"]],
    ["aam-papad",  ["Dried Mango", "Chewy"]],
    ["aampachak",  ["Raw Mango", "Digestive"]],
    ["aam",        ["Raw Mango", "Digestive"]],
    ["mango",      ["Raw Mango", "Digestive"]],
    ["amla",       ["Vitamin C", "Tangy"]],
    ["hing",       ["Beats Gas", "Ayurvedic"]],
    ["anardana",   ["Pomegranate", "Chatpata"]],
    ["anar",       ["Pomegranate", "Chatpata"]],
    ["jaljira",    ["Cooling", "Cumin"]],
    ["jal-jira",   ["Cooling", "Cumin"]],
    ["tikiya",     ["Spiced", "After-meal"]],
    ["tasty",      ["Spiced", "After-meal"]],
    ["mukhwas",    ["Mouth Fresh", "Fennel"]],
    ["saunf",      ["Mouth Fresh", "Fennel"]]
  ];
  var DEFAULT_FEATS = ["100% Natural", "No Preservatives"];

  /* ---- token helpers ------------------------------------------------------- */
  function hasToken(tokens, joined, key) {
    if (key.indexOf("-") !== -1) return joined.indexOf(key) !== -1;
    for (var j = 0; j < tokens.length; j++) if (tokens[j] === key) return true;
    return false;
  }

  function headingOf(card) {
    var link = card.querySelector('[ref="productTitleLink"]');
    var head = link ? link.querySelector('[role="heading"]') : null;
    if (!head) head = card.querySelector('[role="heading"]');
    return head;
  }

  function titleText(card) {
    // Primary: the visible heading paragraph inside the title link.
    var head = headingOf(card);
    if (head && (head.textContent || "").trim()) return (head.textContent || "").trim();
    // Fallback: the title-link anchor text.
    var link = card.querySelector('[ref="productTitleLink"]');
    if (link && (link.textContent || "").trim()) return (link.textContent || "").trim();
    // Fallback: any product link text.
    var a = card.querySelector('a[href*="/products/"]');
    if (a && (a.textContent || "").trim()) return (a.textContent || "").trim();
    return "";
  }

  function handleOf(card) {
    var a = card.querySelector('[ref="productTitleLink"]') || card.querySelector('a[href*="/products/"]');
    if (!a) return "";
    var href = a.getAttribute("href") || a.href || "";
    var m = /\/products\/([^/?#]+)/.exec(String(href));
    return m ? m[1].toLowerCase() : "";
  }

  function tokensFor(card) {
    var txt = titleText(card).toLowerCase();
    if (txt) {
      var t = txt.split(/[\s\-_,/]+/).filter(Boolean);
      if (t.length) return t;
    }
    var h = handleOf(card);
    if (h) return h.split("-").filter(Boolean);
    return [];
  }

  // Consistency: every card uses ONE brand colour (navy) for the title box, border
  // accent and chips, so a grid of many products reads as a cohesive set rather than
  // a rainbow. The terracotta SAVE pill stays the single accent. (FLAVOURS/BUNDLES are
  // kept for the feature-chip text mapping below.)
  function pickColour(tokens) { return NAVY; }

  function featsFor(tokens) {
    var joined = tokens.join("-"), i;
    for (i = 0; i < FEATURES.length; i++) if (hasToken(tokens, joined, FEATURES[i][0])) return FEATURES[i][1];
    return DEFAULT_FEATS;
  }

  /* ---- price reading (robust; sub-classes treated as unknown) --------------- */
  // Parse a money string into a Number of the displayed major unit ("Rs. 1,299.00"
  // -> 1299). Every comma is a thousands/lakh separator (dropped); a dot is decimal.
  function money(str) {
    if (!str) return NaN;
    var m = String(str).replace(/[\s ]/g, "").match(/\d[\d.,]*/);
    if (!m) return NaN;
    var raw = m[0].replace(/,/g, "");   // drop ALL grouping commas (Western + Indian lakh)
    var v = parseFloat(raw);
    return isFinite(v) ? v : NaN;
  }

  // Find the struck compare-at element within a price root, if any. Real sale
  // signal only: a <s>, a <del>, or a compare/was-ish class. We deliberately do
  // NOT infer a sale from "two distinct money values" — a variant price RANGE
  // (min-max) shows two values and is NOT a sale.
  function struckIn(priceRoot) {
    if (!priceRoot || !priceRoot.querySelector) return null;
    return priceRoot.querySelector('s, del, [class*="compare"], [class*="was"]');
  }

  // A leaf is a "unit price" reference (e.g. "Rs. 4.00/100g") — Horizon renders one
  // for weight-priced SKUs, which BCP sells (grams). We must NOT mistake it for the
  // sale price. Detect by a trailing per-measure token in the text, OR by a
  // unit/per/measurement ancestor class.
  function isUnitPriceLeaf(el) {
    try {
      var t = (el.textContent || "");
      if (/\/\s*\d*\s*(g|kg|mg|ml|cl|l|oz|lb|ct|pc|pcs|each)\b/i.test(t)) return true;
      if (/\bper\b/i.test(t)) return true;
      var node = el;
      for (var d = 0; node && d < 4; d++) {
        var cls = (node.className && node.className.baseVal !== undefined)
                    ? node.className.baseVal : (node.className || "");
        if (typeof cls === "string" && /unit|per-|measurement|__unit|per_item/i.test(cls)) return true;
        node = node.parentNode;
      }
    } catch (e) {}
    return false;
  }

  // The current (discounted) price element. Robust choice among non-struck leaves:
  //   - skip the struck element / its descendants
  //   - skip unit-price reference leaves (Rs. X/100g) — see isUnitPriceLeaf
  //   - skip leaves that contain a '%' (a "-28%" discount BADGE parses to 28)
  //   - require the value to sit STRICTLY BELOW the compare-at value (a real sale
  //     price is < compare; a unit price is far below, a badge is a percentage)
  //   - among survivors, prefer the LARGEST value < compare (the real discounted
  //     price sits just under compare; unit prices sit far below it)
  // 'compare' may be NaN when we only have a struck element with no readable value;
  // in that case we fall back to the smallest positive non-unit, non-percent leaf.
  function currentPriceEl(priceRoot, struckEl, compare) {
    if (!priceRoot || !priceRoot.querySelectorAll) return null;
    var haveCompare = isFinite(compare) && compare > 0;
    var best = null, bestV = haveCompare ? -Infinity : Infinity;
    try {
      var leaves = priceRoot.querySelectorAll("*");
      for (var i = 0; i < leaves.length; i++) {
        var el = leaves[i];
        if (el.children && el.children.length) continue;      // leaf only
        if (struckEl && (el === struckEl || (struckEl.contains && struckEl.contains(el)))) continue;
        var txt = el.textContent || "";
        if (txt.indexOf("%") !== -1) continue;                // discount BADGE, not a price
        if (isUnitPriceLeaf(el)) continue;                    // Rs. X/100g reference
        var v = money(txt);
        if (!(isFinite(v) && v > 0)) continue;
        if (haveCompare) {
          if (v >= compare) continue;                         // sale price must be < compare
          if (v > bestV) { bestV = v; best = el; }            // largest value below compare
        } else {
          if (v < bestV) { bestV = v; best = el; }            // fallback: smallest positive
        }
      }
    } catch (e) {}
    return best;
  }

  // Returns { onSale, save, struckEl, saleEl }.
  function saleInfo(card) {
    // Confine the price scan to a real price container. Prefer <product-price>, then
    // a .price / [class*="price"] element inside the content panel; if none exists,
    // bail out (no sale) rather than scanning the whole card and risking an unrelated
    // 'price'-substring node polluting the read.
    var content = card.querySelector(".product-card__content") || card;
    var priceRoot = card.querySelector("product-price") ||
                    content.querySelector('.price, [class*="price"]');
    var out = { onSale: false, save: null, struckEl: null, saleEl: null };
    if (!priceRoot) return out;

    var struckEl = struckIn(priceRoot);
    var compare = struckEl ? money(struckEl.textContent) : NaN;
    if (!(isFinite(compare) && compare > 0)) return out;   // no real compare -> not on sale

    var saleEl = currentPriceEl(priceRoot, struckEl, compare);
    var sale = saleEl ? money(saleEl.textContent) : NaN;
    if (!(isFinite(sale) && sale > 0)) {
      // Struck value exists but no readable current price: still a sale, no pct.
      out.onSale = true; out.struckEl = struckEl; out.saleEl = null;
      out.save = "SALE";
      return out;
    }
    if (!(compare > sale)) return out;                      // struck not higher -> ignore

    out.onSale = true; out.struckEl = struckEl; out.saleEl = saleEl;
    // Full SAVE-label chain per the brief: SAVE % -> SAVE Rs.X -> "SALE".
    var pct = Math.round((compare - sale) / compare * 100);
    if (isFinite(pct) && pct >= 1 && pct < 100) {
      out.save = "SAVE " + pct + "%";
    } else {
      out.save = null;   // negligible discount (<1%, e.g. Re.1) -> no SAVE pill
    }
    return out;
  }

  /* ---- DOM builders (idempotent) ------------------------------------------- */
  function wrapTitle(card) {
    var head = headingOf(card);
    if (!head) return;
    if (head.querySelector(".bcp-title-box")) { head.classList.add("bcp-titled"); return; }
    if (!head.firstChild) { head.classList.add("bcp-titled"); return; }
    // Move existing child nodes into the box span (keeps role on the heading).
    var span = document.createElement("span");
    span.className = "bcp-title-box";
    while (head.firstChild) span.appendChild(head.firstChild);
    head.appendChild(span);
    head.classList.add("bcp-titled");
  }

  function injectSave(card, label) {
    if (!label) return;
    if (card.querySelector(":scope > .bcp-save")) return;
    var s = document.createElement("span");
    s.className = "bcp-save";
    s.setAttribute("aria-hidden", "true"); // decorative; the discount is in the price
    s.textContent = label;
    // Absolute child of the (position:relative) card. NOTE: kept a DIRECT child of
    // product-card — NOT inside .price / product-price — so the CSS price-colour
    // rules never re-tint it and the AA terracotta fill survives. Do not reparent.
    card.appendChild(s);
  }

  function tagPrices(sale) {
    // Robust colour hooks so the CSS can style struck vs sale independent of
    // Horizon's own price sub-classes. Purely additive classes; no reparenting.
    try {
      if (sale.struckEl && sale.struckEl.classList) sale.struckEl.classList.add("bcp-cmp");
      if (sale.saleEl && sale.saleEl.classList) sale.saleEl.classList.add("bcp-sale");
    } catch (e) {}
  }

  // Resolve the block-level ancestor of the heading to insert the chip row after:
  // the outermost node still inside .product-card__content that contains the
  // heading. This reliably lands the chip row UNDER the title, inside the panel.
  function featsAnchor(card, head) {
    var content = card.querySelector(".product-card__content") || card;
    var node = head;
    while (node && node.parentNode && node.parentNode !== content && node.parentNode !== card) {
      node = node.parentNode;
    }
    if (!node || !node.parentNode) return null;
    return node;
  }

  function injectFeats(card, tokens) {
    var head = headingOf(card);
    if (!head) return;
    var anchor = featsAnchor(card, head);
    if (!anchor || !anchor.parentNode) return;
    // Search the WHOLE card, not just anchor.parentNode's direct children: once
    // groupHeader() has run, the existing .bcp-feats lives nested one level deeper
    // inside .bcp-header, so a direct-child-only check here would miss it and inject
    // a duplicate stray row on any re-enhancement pass (e.g. after Horizon strips just
    // the title-box and stillEnhanced() triggers a rebuild).
    if (card.querySelector(".bcp-feats")) return;
    var feats = featsFor(tokens);
    if (!feats || !feats.length) return;
    var wrap = document.createElement("div");
    wrap.className = "bcp-feats";
    wrap.setAttribute("aria-hidden", "true"); // decorative marketing chips
    for (var i = 0; i < feats.length && i < 2; i++) {
      var c = document.createElement("span");
      c.className = "bcp-feat";
      c.textContent = feats[i];
      wrap.appendChild(c);
    }
    anchor.parentNode.insertBefore(wrap, anchor.nextSibling);
  }

  // Group the title heading + the .bcp-feats chip row into ONE div.bcp-header so the
  // CSS terracotta left-spine renders as a single seamless rule and the name reads as
  // a standout "product header" with its attributes tied beneath it. Idempotent;
  // mirrors the injectFeats insertion/anchor pattern. Fully reversed in stripEnhancement.
  function groupHeader(card) {
    try {
      var existing = card.querySelector(".bcp-header");
      var feats = card.querySelector(".bcp-feats");
      if (existing) {
        // Heal a partial re-render: if a later injectFeats() pass created .bcp-feats
        // as a sibling of an already-existing (but feats-less) .bcp-header, pull it
        // inside so the spine still spans name+chips as one continuous rule.
        if (feats && !existing.contains(feats)) existing.appendChild(feats);
        return;
      }
      var head = headingOf(card);
      if (!head) return;
      var anchor = featsAnchor(card, head);   // block child of content that holds the heading
      if (!anchor || !anchor.parentNode) return;
      var wrap = document.createElement("div");
      wrap.className = "bcp-header";
      anchor.parentNode.insertBefore(wrap, anchor);
      wrap.appendChild(anchor);
      if (feats) wrap.appendChild(feats);
    } catch (e) {}
  }

  /* ---- Add-to-Cart button (browse cards) ----------------------------------- */
  var _variantCache = {};
  function productUrl(card) {
    var a = card.querySelector('[ref="productTitleLink"]') || card.querySelector('a[href*="/products/"]');
    var href = a ? (a.getAttribute("href") || a.href || "") : "";
    var m = /\/products\/[^?#]+/.exec(String(href));
    return m ? m[0] : "";
  }
  function resolveVariant(url) {
    if (_variantCache[url] !== undefined) return Promise.resolve(_variantCache[url]);
    return fetch(url + ".js", { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var v = null, i;
        if (p && p.variants && p.variants.length) {
          for (i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { v = p.variants[i].id; break; } }
          if (!v) v = p.variants[0].id;
        }
        _variantCache[url] = v;
        return v;
      });
  }
  function cartAdd(vid) {
    return fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }) });
  }

  /* ---- Pack / size tag (browse cards) ------------------------------------- */
  // Lazily fetch the product JSON (cached) and inject a compact size/pack chip as
  // the FIRST chip in the .bcp-feats row, so a shopper sees the packet size at a
  // glance without opening the product. Prefer an explicit "Pack of N" from the
  // title (combos / sachet multipacks); otherwise show the weight size(s) from the
  // Size option ("125g" or "125g–200g"). Fully guarded + idempotent; the chip lives
  // INSIDE .bcp-feats so the cart scope-guard hides it and stripEnhancement removes
  // it with the row, exactly like the feature chips — no new cleanup path needed.
  var _prodCache = {};
  function fetchProduct(url) {
    if (_prodCache[url] !== undefined) return Promise.resolve(_prodCache[url]);
    return fetch(url + ".js", { headers: { "Accept": "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (p) { _prodCache[url] = p; return p; })
      .catch(function () { _prodCache[url] = null; return null; });
  }
  function packLabel(p) {
    try {
      if (!p) return null;
      var title = p.title || "";
      var vt = (p.variants || [])
        .map(function (v) { return (v.title || "").trim(); })
        .filter(function (t) { return t && t.toLowerCase() !== "default title"; });
      var uniq = [];
      vt.forEach(function (t) { if (uniq.indexOf(t) === -1) uniq.push(t); });

      // Parse a weight (grams) + optional per-pack multiplier out of each variant title.
      // Covers the store's three weight formats: "600g", "125g" and "125g X 2 Packs".
      var parsed = [];
      uniq.forEach(function (t) {
        var gm = /(\d+)\s*(?:g|kg|ml|l)\b/i.exec(t);
        if (!gm) return;
        var mm = /[x×]\s*(\d+)/i.exec(t);
        parsed.push({ g: parseInt(gm[1], 10), m: mm ? parseInt(mm[1], 10) : 1 });
      });
      var weightStr = null, mult = 1;
      if (parsed.length) {
        var gs = parsed.map(function (x) { return x.g; });
        var gmin = Math.min.apply(null, gs), gmax = Math.max.apply(null, gs);
        var mults = parsed.map(function (x) { return x.m; });
        mult = mults.every(function (mm) { return mm === mults[0]; }) ? mults[0] : 1;
        var base = (gmin === gmax) ? (gmin + "g") : (gmin + "–" + gmax + "g");
        weightStr = (mult > 1) ? (base + "×" + mult) : base;
      }

      // Pack count from the title ("Pack of 3", "6 Pack Combo").
      var pm = /pack of\s*(\d+)/i.exec(title) || /(\d+)\s*[- ]?pack\b/i.exec(title);
      var count = pm ? ("Pack of " + pm[1]) : null;
      if (!count) {
        // Multi-flavour combos: count the comma-listed items after the name separator.
        var parts = title.split(/[-–—:]/);
        if (parts.length > 1) {
          var items = parts.slice(1).join("-").split(",").map(function (x) { return x.trim(); }).filter(Boolean);
          if (items.length >= 2) count = items.length + " items";
        }
      }

      // Compose: a "125g×2" weight already encodes the pack count, so show it alone;
      // otherwise pair the count with the weight ("Pack of 2 · 600g"); else fall back to
      // whichever single piece exists (price-point packs have no gram weight to show).
      if (weightStr && mult > 1) return weightStr;
      if (count && weightStr) return count + " · " + weightStr;
      if (count) return count;
      if (weightStr) return weightStr;
      if (uniq.length === 1) return uniq[0];
      return null;
    } catch (e) { return null; }
  }
  function injectPackTag(card) {
    try {
      if (inCart(card)) return;
      if (card.querySelector(".bcp-pack")) return;
      var url = productUrl(card);
      if (!url) return;
      fetchProduct(url).then(function (p) {
        if (!p || inCart(card) || card.querySelector(".bcp-pack")) return;
        var label = packLabel(p);
        if (!label) return;
        var feats = card.querySelector(".bcp-feats");
        if (!feats) return;
        var chip = document.createElement("span");
        chip.className = "bcp-feat bcp-pack";
        chip.setAttribute("aria-hidden", "true");
        chip.textContent = label;
        feats.insertBefore(chip, feats.firstChild);
      });
    } catch (e) {}
  }
  // Horizon's own real, fully-wired quick-add submit button (rendered by
  // snippets/quick-add.liquid on every card - settings.quick_add defaults to true).
  // Clicking it runs product-form-component's real submit handler, which dispatches
  // the actual cart-update event Horizon's cart-icon bubble and cart-items-component
  // (drawer + /cart page) listen for, and morphs their DOM accordingly - the exact
  // reactive behavior a plain fetch('/cart/add.js') from us never triggers, since
  // those components only react to a mutation THEY dispatched, not any /cart/add.js
  // call. Returns null (never disabled) so callers fall back to the direct fetch path.
  //
  // MUST-FIX: both the "Add" and "Choose" buttons are ALWAYS in the DOM per Horizon's
  // own quick-add.liquid comment - only one is SHOWN, via CSS keyed off a
  // data-quick-add-button="add"/"choose" state, never by omitting the other from the
  // DOM. For a multi-variant product with no swatch/variant resolved, Horizon hides
  // "Add" via display:none and shows "Choose" instead (so the shopper picks a variant
  // via a modal) - CSS display:none never sets the DOM `disabled` property, so a
  // disabled-only check would treat that hidden, wrong-for-this-product control as
  // live. Programmatic .click() ignores visibility entirely (that restriction only
  // applies to real pointer events), so clicking it would silently submit whatever
  // default variant sits in the hidden id input, with no variant picker ever shown -
  // worse than the old fallback, since it ALSO fires a real cart-update event and a
  // false "Added" state for an add the shopper never confirmed. Reject anything that
  // isn't genuinely the live, shopper-visible control.
  function nativeAddBtn(card) {
    var b = card.querySelector(".quick-add__button--add");
    if (!b || b.disabled) return null;
    if (b.offsetParent === null) return null; // covers display:none up the ancestor chain
    var cs = window.getComputedStyle(b);
    if (cs.display === "none" || cs.visibility === "hidden") return null;
    var chooseHost = card.querySelector('[data-quick-add-button="choose"]');
    if (chooseHost && chooseHost.contains(b)) return null;
    return b;
  }
  function openDrawer() {
    try {
      var t = document.querySelector('cart-icon, .cart-icon, [aria-controls*="cart" i], a[href="/cart"], a[href$="/cart"]');
      if (t && t.click) t.click();
    } catch (e) {}
  }
  function flashBtn(btn, text) {
    if (btn.getAttribute("data-lbl") == null) btn.setAttribute("data-lbl", btn.textContent);
    btn.textContent = text; btn.classList.add("bcp-buy--done");
    setTimeout(function () { btn.textContent = btn.getAttribute("data-lbl"); btn.classList.remove("bcp-buy--done"); }, 1400);
  }
  function injectBuy(card) {
    if (inCart(card)) return;
    var content = card.querySelector(".product-card__content") || card;
    if (card.querySelector(".bcp-buy")) return;
    var url = productUrl(card);
    if (!url) return;
    var row = document.createElement("div"); row.className = "bcp-buy";
    var atc = document.createElement("button"); atc.type = "button"; atc.className = "bcp-buy__btn bcp-atc"; atc.textContent = "Add to Cart";
    row.appendChild(atc); content.appendChild(row);
    atc.addEventListener("click", function (e) {
      e.preventDefault(); e.stopPropagation();
      if (atc.disabled) return; atc.disabled = true;
      var native = nativeAddBtn(card);
      if (native) {
        // Delegate the actual mutation to Horizon's own control so its cart-icon
        // bubble and drawer/cart-page line list update WITHOUT a manual refresh.
        // Our button just mirrors the success state a beat later (Horizon's own
        // button resets its "added" animation after ~800ms).
        native.click();
        setTimeout(function () { atc.disabled = false; flashBtn(atc, "Added ✓"); openDrawer(); }, 500);
        return;
      }
      // Fallback only (native control missing/disabled) - previous direct-add path.
      resolveVariant(url).then(function (vid) {
        if (!vid) { atc.disabled = false; window.location = url; return; }
        cartAdd(vid).then(function (r) {
          atc.disabled = false;
          if (r && r.ok) { flashBtn(atc, "Added ✓"); openDrawer(); } else { window.location = url; }
        }).catch(function () { atc.disabled = false; window.location = url; });
      }).catch(function () { atc.disabled = false; window.location = url; });
    });
  }

  /* Belt-and-braces: strip any .bcp-bn left in the DOM by a cached copy of this
     file or a stale server-rendered card. Cheap, idempotent, runs with the rest. */
  function dropLegacyBuyNow(root) {
    var stale = (root || document).querySelectorAll(".bcp-bn");
    for (var i = 0; i < stale.length; i++) {
      try { stale[i].parentNode.removeChild(stale[i]); } catch (e) {}
    }
  }

  /* ---- guards -------------------------------------------------------------- */
  var CART_SEL = "cart-drawer, cart-drawer-component, cart-items, cart-items-component, " +
                 '[class*="cart-drawer"], [class*="cart-items"], dialog';
  function inCart(el) {
    return !!(el && el.closest && el.closest(CART_SEL));
  }
  var OWN_SEL = ".bcp-save, .bcp-feats, .bcp-title-box, .bcp-header";
  function isOwn(el) {
    return !!(el && el.closest && el.closest(OWN_SEL));
  }

  /* ---- enhancement presence check (must-fix: in-place re-render recovery) --- */
  // A card is considered "still enhanced" only if it carries .bcp-hz AND its
  // heading still contains our .bcp-title-box. When Horizon replaces a card's
  // INNER DOM but keeps the same <product-card> element (data-bcp-done survives),
  // these marks are gone; we detect that and re-run the idempotent builders.
  function stillEnhanced(card) {
    if (!card.classList || !card.classList.contains("bcp-hz")) return false;
    var head = headingOf(card);
    if (!head) return false;
    if (!head.querySelector(".bcp-title-box")) return false;
    // A .bcp-header that survived but no longer contains a .bcp-feats means a partial
    // in-place re-render stripped/detached the chip row without wiping the title-box;
    // force a rebuild pass (groupHeader() heals this) instead of treating it as done.
    // Harmless no-op re-run for products with zero chips by design (all builders below
    // are idempotent, and the MutationObserver only watches childList, not attributes,
    // so this can never retrigger itself).
    var hdr = card.querySelector(".bcp-header");
    if (hdr && !hdr.querySelector(".bcp-feats")) return false;
    return true;
  }

  /* ---- cart sanitiser (must-fix: card MOVED into the cart after enhancement) --- */
  // If a <product-card> that we enhanced in a grid is later reused/reparented into
  // the cart drawer, cart-items, or a dialog, strip everything we added so it can
  // never render our frame/box/pill there. Cart placement is AUTHORITATIVE and this
  // runs BEFORE the data-bcp-done early return, so a moved card is always cleaned.
  function stripEnhancement(card) {
    try {
      if (card.classList) { card.classList.remove("bcp-hz"); card.classList.remove("bcp-onsale"); }
      if (card.removeAttribute) { card.removeAttribute("data-bcp-teal"); }
      if (card.style && card.style.removeProperty) {
        card.style.removeProperty("--bcp-pcolor");
        card.style.removeProperty("--bcp-ptext");
      }
      // Unwrap the header group FIRST: move the title block + chip row back out to
      // where the wrapper sat, then drop the wrapper. This restores the original DOM
      // so the title-box / feats cleanup below runs exactly as it did pre-grouping.
      var hdr = card.querySelector(".bcp-header");
      if (hdr && hdr.parentNode) {
        while (hdr.firstChild) hdr.parentNode.insertBefore(hdr.firstChild, hdr);
        hdr.parentNode.removeChild(hdr);
      }
      // Remove injected decorative nodes (safe: they are ours, never Horizon DOM).
      var save = card.querySelector(":scope > .bcp-save");
      if (save && save.parentNode) save.parentNode.removeChild(save);
      var feats = card.querySelectorAll(".bcp-feats");
      for (var i = 0; i < feats.length; i++) {
        if (feats[i].parentNode) feats[i].parentNode.removeChild(feats[i]);
      }
      var buys = card.querySelectorAll(".bcp-buy");
      for (var bi = 0; bi < buys.length; bi++) { if (buys[bi].parentNode) buys[bi].parentNode.removeChild(buys[bi]); }
      // Unwrap the title box: move its child nodes back into the heading, drop the
      // span + our clamp class. NEVER touches the price / gallery / card link.
      var head = headingOf(card);
      if (head) {
        var box = head.querySelector(".bcp-title-box");
        if (box && box.parentNode) {
          while (box.firstChild) box.parentNode.insertBefore(box.firstChild, box);
          box.parentNode.removeChild(box);
        }
        if (head.classList) head.classList.remove("bcp-titled");
      }
    } catch (e) {}
  }

  /* ---- per-card enhance ---------------------------------------------------- */
  function enhance(card) {
    try {
      if (!card) return;

      // Cart placement is AUTHORITATIVE and checked FIRST — before the
      // data-bcp-done gate — so a card enhanced in a grid and then moved into the
      // cart drawer / cart-items / a dialog is sanitised, not left with stale
      // .bcp-hz. This keeps the JS gate and the CSS guard consistent.
      if (inCart(card)) {
        if (card.getAttribute("data-bcp-cart") !== "1") stripEnhancement(card);
        card.setAttribute("data-bcp-cart", "1");
        card.removeAttribute("data-bcp-done");
        return;
      }
      // If a card previously flagged as in-cart is now back in a grid, clear the
      // stale cart flag so it can be enhanced normally below.
      if (card.getAttribute("data-bcp-cart") === "1") card.removeAttribute("data-bcp-cart");

      if (card.getAttribute("data-bcp-done") === "1") {
        // Cheap re-verify: if the marks survived, we're done. If an in-place
        // re-render stripped them, clear the gate and fall through to re-enhance
        // (all builders below are idempotent).
        if (stillEnhanced(card)) return;
        card.removeAttribute("data-bcp-done");
      }

      var tokens = tokensFor(card);
      var colour = pickColour(tokens);
      card.style.setProperty("--bcp-pcolor", colour.c);
      card.style.setProperty("--bcp-ptext", colour.t);
      card.classList.add("bcp-hz");
      if (colour.c === TEAL) card.setAttribute("data-bcp-teal", "1");

      wrapTitle(card);
      injectFeats(card, tokens);
      groupHeader(card);
      injectPackTag(card);
      injectBuy(card);

      var s = saleInfo(card);
      if (s.onSale) {
        card.classList.add("bcp-onsale");
        tagPrices(s);
        injectSave(card, s.save);
      }

      card.setAttribute("data-bcp-done", "1");
    } catch (e) { /* never break the card */ }
  }

  // Enhance every card in a scope, including cards whose data-bcp-done survived an
  // in-place re-render (so we must NOT gate on :not([data-bcp-done]) here — the
  // presence re-verify inside enhance() decides whether work is actually needed).
  function scanAll(scope) {
    var list = scope.querySelectorAll("product-card");
    for (var i = 0; i < list.length; i++) enhance(list[i]);
  }

  function scan(root) {
    try {
      var scope = (root && root.querySelectorAll) ? root : document;
      scanAll(scope);
      dropLegacyBuyNow(scope);
      if (root && root.nodeType === 1 && root.matches && root.matches("product-card")) {
        enhance(root);
      }
    } catch (e) {}
  }

  /* ---- observer (debounced) ------------------------------------------------ */
  var timer = null, pending = [], sweeping = false;
  var needSweep = false;           // a non-product-card node was queued -> possible in-place re-render
  var lastSweep = 0;               // throttle the full-document sweep to <= once/second

  function isCardNode(n) {
    return !!(n && n.nodeType === 1 && n.matches && n.matches("product-card"));
  }

  function now() {
    return (window.Date && Date.now) ? Date.now() : +new Date();
  }

  function flush() {
    timer = null;
    if (sweeping) return;
    sweeping = true;
    try {
      var nodes = pending; pending = [];
      // Fast path (unconditional): enhance each queued node's subtree.
      for (var i = 0; i < nodes.length; i++) scan(nodes[i]);
      // Full-document safety net: recovers cards whose INNER DOM re-rendered in
      // place (marks stripped, data-bcp-done possibly stale). Gated so a large
      // collection page does not thrash: only when a non-card node was queued
      // (needSweep) AND at most once per second.
      if (needSweep) {
        var elapsed = now() - lastSweep;
        if (elapsed >= 1000) {
          lastSweep = now();
          scan(document);
          needSweep = false;              // cleared ONLY after an ACTUAL sweep ran
        } else if (!timer) {
          // MUST-FIX: throttled out. Do NOT drop needSweep — a re-render whose
          // sweep request lands inside the 1s window must still run. Re-arm a
          // trailing flush for the remaining cooldown so the deferred sweep fires
          // even if no further mutation arrives to re-trigger the observer.
          timer = setTimeout(flush, (1000 - elapsed) + 20);
        }
      }
    } catch (e) {}
    sweeping = false;
  }
  function schedule(node) {
    if (node) {
      pending.push(node);
      if (!isCardNode(node)) needSweep = true;   // subtree add -> maybe an in-place re-render
    }
    if (timer) return;
    timer = setTimeout(flush, 120);
  }

  function onMutations(muts) {
    try {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        if (isOwn(m.target) || inCart(m.target)) continue;
        if (m.addedNodes && m.addedNodes.length) {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var n = m.addedNodes[j];
            if (n.nodeType === 1 && !isOwn(n) && !inCart(n)) schedule(n);
          }
        }
      }
    } catch (e) {}
  }

  function observe() {
    try {
      var mo = new MutationObserver(onMutations);
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  function start() {
    scan(document);
    observe();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();

